import React, { useCallback, useState, useEffect } from 'react';
import { useSafeContext, createNamedContext } from '@clover-util/react-context';

import { useApplePayCosRequests } from './useApplePayCosRequests';
import { getIdentifier } from './ApplePayIdentifierUtil';
import { useMerchant } from 'hooks/context/merchant';
import { usePostApplePayToken } from 'hooks/useApi/usePostApplePayToken';
import { APPLE_PAY_V2_ENABLED } from 'hooks/context/merchant/models';
import { usePutApplePayOrderPayment } from 'hooks/useApi/usePutApplePayOrderPayment';
import { Customer, OrderReceipt } from 'olo-service';
import { useTableOrderPaymentsRequest } from 'hooks/useApi';
import { useCustomer } from 'hooks/context/customer/customer';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useGiftCard } from 'routes/Checkout/context/GiftCard/GiftCardContext';

type ApplePayState = {
  isApplePayEnabled: boolean;
  payWithApplePay: (session: ApplePaySession, receiptId: string, tipAmount: number, tableUuid?: string, customer?: Customer, captchaToken?: string) => Promise<OrderReceipt | undefined>;
}

const ApplePayContext = createNamedContext<ApplePayState>('ApplePayContext');

export function useApplePay() {
  return useSafeContext(useApplePay, ApplePayContext);
}

export const ApplePayProvider: React.FC = ({ children }) => {
  const [isEnabled, setEnabled] = useState(false);
  const { isSettingEnabled, merchant } = useMerchant();
  const { postApplePayToken } = usePostApplePayToken();
  const { validateMerchant, payPublicly, getWebPayToken } = useApplePayCosRequests();
  const { showRunningOrderState } = useMerchantState();
  const { putApplePayOrderPayment } = usePutApplePayOrderPayment(merchant?.merchantUuid);
  const { sessionToken } = useCustomer();
  const postTableOrdersImpl = useTableOrderPaymentsRequest(merchant?.merchantUuid, sessionToken);
  const { appliedGiftCards } = useGiftCard();
  useEffect(() => {
    if (!window.ApplePaySession) return;

    try {
      if (!ApplePaySession.canMakePayments()) return;

      ApplePaySession.canMakePaymentsWithActiveCard(getIdentifier(process.env.NODE_ENV)).then(setEnabled, e => {
        console.warn('ApplePay', e);
        setEnabled(false);
      });
    } catch (e) {
      console.warn('ApplePay', e);
    }
  }, []);

  const pay = useCallback((
    session: ApplePaySession,
    receiptId: string,
    tipAmount: number,
    tableUuid?: string,
    customer?: Customer,
    captchaToken?: string
  ) => getWebPayToken(receiptId).then(token => new Promise<OrderReceipt | undefined>((resolve, reject) => {
      if (!token) {
        reject({ message: 'Apple Pay: Web Pay Token is undefined' });

        return;
      }

      // eslint-disable-next-line functional/immutable-data
      session.oncancel = event => {
        const message = 'Apple Pay: Payment was canceled';

        window.console.error(message);

        reject({ message, event });
      };

      /**
       * Merchant Validation
       * We call our merchant session endpoint, passing the URL to use
       * URL should look something like - validationURL: "https://apple-pay-gateway-cert.apple.com/paymentservices/startSession"
       */
      // eslint-disable-next-line functional/immutable-data
      session.onvalidatemerchant = ({ validationURL }) => {
        validateMerchant(validationURL, token)
          .then(data => session.completeMerchantValidation(data))
          .catch(reject);
      };

      const payForOrder = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent): Promise<OrderReceipt | undefined> => {
        if (!isSettingEnabled(APPLE_PAY_V2_ENABLED)) {
          // The token here refers to the Apple Pay token object, not a Clover token
          return payPublicly(event, receiptId, token, tipAmount);
        }
        const cToken = await postApplePayToken(event);
        if (!cToken) {
          throw new Error('Could not tokenize Apple Pay session');
        }

        try {
          if (showRunningOrderState && receiptId) {
            return await postTableOrdersImpl({
              tableUuid,
              paymentToken: cToken,
              tipValue: tipAmount,
              customer,
              captchaToken,
              appliedGiftCards
            });
          } else {
            return await putApplePayOrderPayment<OrderReceipt>(cToken, receiptId);
          }
        } catch (error) {
          throw new Error( 'Apple Pay: Payment Declined');
        }

      };

      // eslint-disable-next-line functional/immutable-data
      session.onpaymentauthorized = event => {
        payForOrder(event)
          .then((orderReceipt) => {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);

            resolve(orderReceipt);
          })
          .catch((e: any) => {
            session.completePayment(ApplePaySession.STATUS_FAILURE);

            reject(e);
          });
      };

      // All our handlers are setup - start the Apple Pay payment
      session.begin();
  })), [getWebPayToken, validateMerchant, isSettingEnabled, postApplePayToken, showRunningOrderState, payPublicly, postTableOrdersImpl, putApplePayOrderPayment]);

  return (
    <ApplePayContext.Provider value={{
      isApplePayEnabled: isEnabled,
      payWithApplePay: pay
    }}>
      {children}
    </ApplePayContext.Provider>
  );
};
